import { inject } from '@angular/core';
import {
  MMU_APP_CONTEXT,
  MmuFeatureFlagsService,
  MmuMainMenuMainEntry,
  MmuUserInfoDetails,
  MmuUserRoles,
  MmuUserService
} from '@move/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { customerMenuItems } from './customer-menu-items';
import { serviceMenuItems } from './service-menu-items';


export const mmuItemsFactory = (): Observable<MmuMainMenuMainEntry[]> => {
  const userService = inject(MmuUserService);
  const appContext = inject(MMU_APP_CONTEXT);
  const ffs = inject(MmuFeatureFlagsService);

  const shouldDisplayMenuEntry = (menuItem: MmuMainMenuMainEntry, user?: MmuUserInfoDetails): boolean => {
    const { hidden, hideFeature, feature, roles } = menuItem;
    const hasWatsonTestFF = Array.isArray(feature)
                            ? feature.includes('watsonTestUser')
                            : feature === 'watsonTestUser';

    /* @deprecated FF_watsonTestUser: We need a custom temporary solution for watson, because we want to show/hide menu
     * items based on a feature flag and AT THE SAME TIME overwrite every feature flag rule for test users with a
     * second feature flag (FF_watsonTestUser). If the latter is enabled, we ignore every other rule and show the menu
     * item regardless of the feature flag or role.
     *
     * See https://miele.atlassian.net/browse/MPROTAL-46201
     *  */
    if (feature && hasWatsonTestFF) {
      if (ffs.isFeatureEnabled('watsonTestUser')) {
        return true;
      }
    }

    // Feature flags related check
    if (hidden) {
      if (!user?.subsidiary) {
        return false;
      }

      return !ffs.isFeatureEnabled(`${hidden}_${user.subsidiary.toUpperCase()}`);
    }

    if (hideFeature) {
      /* If one or more hideFeature is enabled, the menu item should be hidden */
      if (Array.isArray(hideFeature)) {
        return !hideFeature.some((feature) => ffs.isFeatureEnabled(feature));
      } else {
        return !ffs.isFeatureEnabled(hideFeature);
      }
    }

    if (feature) {
      /* One or more feature must be enabled to show the menu item */
      if (Array.isArray(feature)) {
        return feature.some((feature) => ffs.isFeatureEnabled(feature));
      } else {
        return ffs.isFeatureEnabled(feature);
      }
    }

    if (roles) {
      /* Check if the user has one of the required roles */
      if (!user?.roles?.length) {
        return false;
      }
      return user.roles.some((userRole) => roles.includes(userRole as MmuUserRoles));
    }

    // If none of the above conditions are set, the menu item is shown by default
    return true;
  };

  return userService.user$.pipe(
    map((user): MmuMainMenuMainEntry[] => {
      const menuItems = user.type === 'service' ? serviceMenuItems(appContext) : customerMenuItems(appContext);

      // Filter all items and subitems based on feature flags and user roles/subsidiaries
      return menuItems.filter((item) => {
        if (item.children) {
          item.children = item.children.filter((child) => shouldDisplayMenuEntry(child, user));
        }
        return shouldDisplayMenuEntry(item, user);
      });
    })
  );
};
